/**
 * Hero + HeroStage — first screen of the landing (mockup #top + the product stage).
 * The stage uses a local `.iphone` frame (not the shared iPhoneFrame): the shared one
 * centres the pass vertically, which pushes the QR out of the 500px crop.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Add01Icon, Call02Icon, Mail01Icon, QrCodeScanIcon, Tick02Icon } from '@hugeicons/core-free-icons';
import Icon from '../../components/v2/Icon.jsx';
import { APP_STORE_URL } from '../../lib/appStore.js';
import { useStr } from './useStr.js';
import { ME, ME_DARK, PHOTO_URL } from './data.js';
import PassSlot from './PassSlot.jsx';

const L = ({ children, className = '' }) => <span className={`ltr${className ? ` ${className}` : ''}`}>{children}</span>;

export function Hero() {
  const { t, ar, arrow } = useStr();
  return (
    <section className="hero" id="top">
      <div className="wrap">
        <a className="announce" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label={t('announceAria')}>
          <span className="orb o1" />
          {ar
            ? <span><b>جديد:</b> تطبيق كارد باس صار على <L>App Store</L></span>
            : <span><b>New:</b> CardPass is now on the App Store</span>}
          <span className="go" data-dir={ar ? 'rtl' : 'ltr'}><Icon icon={arrow} /></span>
        </a>
        <h1 className="display">{t('heroTitle')}</h1>
        <p className="lead">
          {ar ? (
            <>
              بطّل تطبع كروت ما أحد يحتفظ فيها. سوّ بطاقتك في <span className="nw"><L className="num">30</L> ثانية</span>، وخلّها تعيش في{' '}
              <span className="nw">Apple Wallet و Google Wallet</span>: <span className="nw">دايمًا معك</span>، تتحدّث تلقائيًا، وتنشارك بنقرة.
            </>
          ) : (
            <>
              Stop printing cards no one keeps. Make your card in <span className="nw">30 seconds</span> and keep it in{' '}
              <span className="nw">Apple Wallet</span> and <span className="nw">Google Wallet</span>: always with you, auto-updating, shared in a tap.
            </>
          )}
        </p>
        <div className="ctas">
          <Link className="btn dark lg" to="/signup">{t('heroCta')}<Icon icon={arrow} /></Link>
          <Link className="btn lg" to="/business">{t('heroBiz')}</Link>
        </div>
        <div className="note">
          {['tick1', 'tick2', 'tick3', 'tick4'].map((k) => (
            <span key={k}><Icon icon={Tick02Icon} />{t(k)}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

const SWATCH_ROW = ['#FFFFFF', '#1c1c20', '#0C44EF', '#0F2E1F', '#3B0A1E'];

export function HeroStage() {
  const { t, ar } = useStr();
  return (
    <section className="wrap" aria-hidden="true">
      <div className="hstage">
        <div className="glow ga orb o1" />
        <div className="glow gb orb o4" />

        <div className="flt edit">
          <div className="flh"><b>{t('edTitle')}</b><span className="live"><i />{t('live')}</span></div>
          <div className="fld">
            <div className="l">{t('fName')}</div>
            <div className="in"><L>{ME.full_name_en}<span className="caret" /></L></div>
          </div>
          <div className="fld">
            <div className="l">{t('fTitle')}</div>
            <div className="in"><L>{ME.title_en}</L></div>
          </div>
          <div className="fld">
            <div className="l">{t('fColor')}</div>
            <div className="sw sm">
              {SWATCH_ROW.map((c) => <i key={c} className={c === '#1c1c20' ? 'on' : undefined} style={{ background: c }} />)}
            </div>
          </div>
        </div>

        <div className="iphone">
          <div className="scr">
            <div className="island" />
            <div className="sb">
              <span>9:41</span>
              <span className="sbr">
                <span className="sig"><i style={{ height: 4 }} /><i style={{ height: 6 }} /><i style={{ height: 8.5 }} /><i style={{ height: 11 }} /></span>
                <span className="bat" />
              </span>
            </div>
            <div className="wh"><span>Wallet</span><span className="b"><Icon icon={Add01Icon} /></span></div>
            <PassSlot card={ME_DARK} photoUrl={PHOTO_URL} scale={0.894} />
          </div>
        </div>

        <div className="flt contact">
          <div className="okr">
            <span className="d"><Icon icon={Tick02Icon} /></span>
            {t('saved')}
            <span className="t"><Icon icon={QrCodeScanIcon} />{ar ? <>بعد مسح الـ <L>QR</L></> : 'after a QR scan'}</span>
          </div>
          <div className="ct">
            <img src={PHOTO_URL} alt="" />
            <div>
              <b className="ltr vr">{ME.full_name_en}</b>
              <span className="ltr vr">{ME.title_en} · {ME.company}</span>
            </div>
          </div>
          <div className="cr"><Icon icon={Call02Icon} /><L className="num">{ME.phone}</L></div>
          <div className="cr"><Icon icon={Mail01Icon} /><L>{ME.email}</L></div>
        </div>
      </div>
    </section>
  );
}
